import {SITE, bioUrl} from "site";

export const PROCESS_STEPS = [
  {
    title: "Draft the change",
    body: "Work starts as a draft pull request so nothing ships by surprise.",
  },
  {
    title: "Preview it live",
    body: "Each pull request gets a live preview before it ships.",
  },
  {
    title: "Review, then squash",
    body: "Humans and automated review read the diff. Merge is squash-only.",
  },
  {
    title: "Production waits for a formal release",
    body:
      "splitrock.dev production updates only after a tagged formal release — not on every merge.",
  },
] as const;

export const CASE_STUDIES = {
  site: {
    title: "This site",
    product: "splitrock.dev",
    brief: "One direct bio for recruiters, with public facts for agents.",
    body:
      "The desktop home links to a direct /bio page with role, stack, contact details, and case studies. /llms.txt and /about.json publish public profile facts for agents. Private internals stay off the site; production updates follow reviewed previews and a tagged release.",
    points: [
      {label: "Need", text: "One direct profile for recruiters"},
      {label: "Approach", text: "Desktop home, /bio, and public facts files"},
      {label: "Constraint", text: "Private internals stay off the site"},
      {label: "Result", text: "Role, contact, and case studies at one URL"},
    ],
    flow: ["Desktop", "/bio", "Public facts", "Contact"],
  },
  appTemplate: {
    title: "App template",
    product: "App template",
    brief: "Kubernetes app template: idea to a live URL for non-engineers.",
    body:
      "Non-engineers needed running prototypes without a custom deploy each time. A Kubernetes app template stands up an environment automatically — idea to a live URL, no hand-built infra per prototype.",
    points: [
      {label: "Need", text: "Prototypes without a custom deploy"},
      {label: "Constraint", text: "No hand-built infra per prototype"},
      {label: "Result", text: "Idea to a live URL on Kubernetes"},
    ],
    flow: ["Idea", "App template", "Kubernetes", "Live URL"],
  },
  myLedger: {
    title: "My Ledger",
    product: "My Ledger",
    brief: "Offline Swift/SwiftUI budgeting. Source is not public.",
    body:
      "Wanted offline budgeting that never required a server. Local-first Swift and SwiftUI. Source is not public; it ships with the same draft-PR and tagged-release discipline as this site.",
    points: [
      {label: "Need", text: "Offline budgeting with no account"},
      {label: "Constraint", text: "Source is not public"},
      {label: "Result", text: "Local-first Swift/SwiftUI, no server"},
    ],
    flow: ["On device", "SwiftUI", "Local store", "No server"],
  },
} as const;

export const CASE_STUDY_LIST = [
  CASE_STUDIES.site,
  CASE_STUDIES.appTemplate,
  CASE_STUDIES.myLedger,
] as const;

export const PROCESS_SUMMARY = PROCESS_STEPS.map(
  (step, index) => `${index + 1}. ${step.title} — ${step.body}`,
).join(" ");

/**
 * Published JSON at /about.json. Keep public/about.json and
 * netlify/functions/about.json identical to this object.
 */
export const ABOUT_DOCUMENT = {
  name: SITE.name,
  jobTitle: SITE.headline,
  description: SITE.description,
  url: SITE.url,
  bio: bioUrl,
  email: SITE.email,
  telephone: SITE.phoneDisplay,
  location: "Greater Boston Area, NH",
  availability: SITE.availability,
  focus: SITE.focus,
  skills: [...SITE.skills],
  skillGroups: SITE.skillGroups.map((group) => ({
    label: group.label,
    items: [...group.items],
  })),
  education: {
    degree: "B.S. Computer Science",
    school: "Rensselaer Polytechnic Institute",
    years: "2018 - 2021",
  },
  roles: [
    {
      org: "Splitrock Consulting",
      title: "Founder",
      summary: "Agentic software, iOS, and custom full-stack systems.",
    },
  ],
  projects: [
    {
      name: "splitrock.dev",
      summary: CASE_STUDIES.site.body,
      url: bioUrl,
    },
    {
      name: "App template",
      summary: CASE_STUDIES.appTemplate.body,
    },
    {
      name: "My Ledger",
      summary: CASE_STUDIES.myLedger.body,
    },
    {
      name: "FIRST Team 95",
      summary: "Programming coach for FRC Team 95, The Grasshoppers.",
      url: SITE.firstGithub,
    },
  ],
  process: {
    summary:
      "Agents are extra hands on a review-gated process, not a bypass.",
    steps: PROCESS_STEPS.map((step) => ({
      title: step.title,
      body: step.body,
    })),
  },
  sameAs: [SITE.linkedin, SITE.github],
  machineReadable: {
    llmsTxt: `${SITE.url}${SITE.llmsPath}`,
    aboutJson: `${SITE.url}${SITE.aboutJsonPath}`,
  },
} as const;

const WORK_AUTH =
  /\b(work[- ]auth|authorized to work|visa|citizenship|green card|sponsorship|start date|can you start|when can you start|salary|comp(ensation)?)\b/i;

function has(q: string, ...needles: string[]): boolean {
  return needles.some((needle) => q.includes(needle));
}

/** Keyword answers grounded only in ABOUT_DOCUMENT / SITE. */
export function answerQuestion(raw: string): string {
  const question = raw.replace(/\s+/g, " ").trim();
  if (!question) {
    return `Ask about Nick, this site, My Ledger, or how work ships. Or email ${SITE.email}.`;
  }

  if (WORK_AUTH.test(question)) {
    return `I don't publish work-authorization, start-date, or compensation details here. Email ${SITE.email}.`;
  }

  const q = question.toLowerCase();

  if (has(q, "resume", "cv")) {
    return `I don't publish a resume here. Email ${SITE.email}.`;
  }

  if (has(q, "email", "contact", "phone", "reach", "hire")) {
    return `Email ${SITE.email} or call ${SITE.phoneDisplay}. LinkedIn: ${SITE.linkedin}. Recruiter card: ${bioUrl}.`;
  }

  if (
    has(
      q,
      "live",
      "location",
      "located",
      "based",
      "boston",
      "where do you live",
      "where are you",
    )
  ) {
    return `${SITE.name} is in the Greater Boston Area, NH.`;
  }

  if (has(q, "school", "college", "rpi", "rensselaer", "education", "degree", "major")) {
    return `B.S. Computer Science, Rensselaer Polytechnic Institute (2018–2021).`;
  }

  if (has(q, "skill", "stack", "tech", "languages")) {
    return `Skills: ${SITE.skills.join(", ")}. Focus: ${SITE.focus}.`;
  }

  if (has(q, "ledger", "budget")) {
    return CASE_STUDIES.myLedger.body;
  }

  if (has(q, "kubernetes", "k8s", "prototype", "app template", "app-template")) {
    return CASE_STUDIES.appTemplate.body;
  }

  if (has(q, "process", "agent", "ship", "preview", "release", "how you work", "how do you work")) {
    return `${ABOUT_DOCUMENT.process.summary} ${PROCESS_SUMMARY}`;
  }

  if (has(q, "logicbroker", "red river", "employer", "job", "where do you work", "who do you work", "role", "title")) {
    return `${SITE.headline}. Founder of Splitrock Consulting. ${SITE.availability}. Email ${SITE.email}.`;
  }

  if (has(q, "splitrock.dev", "this site", "website", "llms", "about.json", "facts")) {
    return CASE_STUDIES.site.body;
  }

  if (has(q, "first", "robotics", "frc", "grasshopper", "95")) {
    return `Programming coach for FIRST Robotics Competition Team 95 (The Grasshoppers). ${SITE.firstGithub}`;
  }

  if (has(q, "diffex")) {
    return `DiffEx is a web app for trading options on Ethereum, built with React, Redux, and Solidity. ${SITE.diffex}`;
  }

  if (has(q, "who", "name", "nick", "bio", "about")) {
    return `${SITE.name} — ${SITE.headline}. ${SITE.description} Recruiter card: ${bioUrl}.`;
  }

  return `I only answer from the public facts on this site. Try work, location, My Ledger, or how changes ship — or email ${SITE.email}.`;
}
